import React from 'react';
import styles from './Ask.module.css';
import * as utils from '../../utils/utils';
import Sidebar from '../Sidebar/Sidebar';
import { FiArrowRight, FiCompass, FiCpu, FiMessageSquare, FiUsers, FiX } from 'react-icons/fi';

export default class Ask extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			isLoading: false,
			isSearchMode: false,
			errorText: '',
			queryText: '',
			parsedQuery: null,
			searchResults: [],
			isFocused: false,
		};
	}

	componentDidMount() {
		if (!utils.isLoggedIn()) {
			window.location.href = '/signin';
			return;
		}
	}

	onQueryTextChange = (event) => {
		this.setState({ queryText: event.target.value });
	};

	onOptionClick = (optionText) => {
		this.setState({ queryText: optionText }, () => {
			this.runNaturalLanguageSearch();
		});
	};

	runNaturalLanguageSearch = () => {
		const query = this.state.queryText.trim();

		if (!query) {
			this.setState({
				errorText: 'Please type a query first.',
			});
			return;
		}

		this.setState({
			isLoading: true,
			errorText: '',
			isSearchMode: true,
		}, async () => {
			try {
				const response = await utils.sendRequest('/ask/ask', {
					query,
				});

				this.setState({
					isLoading: false,
					parsedQuery: response.parsedQuery || null,
					searchResults: Array.isArray(response.results) ? response.results : [],
				});
			} catch (error) {
				this.setState({
					isLoading: false,
					errorText: error && error.message ? error.message : 'Could not run semantic search right now.',
				});
			}
		});
	};

	resetSearchMode = () => {
		this.setState({
			isSearchMode: false,
			parsedQuery: null,
			searchResults: [],
			errorText: '',
			queryText: '',
		});
	};

	onKeyDown = (event) => {
		if (event.key === 'Enter' && !event.shiftKey) {
			event.preventDefault();
			this.runNaturalLanguageSearch();
		}
	};

	onFocus = () => {
		this.setState({ isFocused: true });
	};

	onBlur = () => {
		this.setState({ isFocused: false });
	};

	formatDateTime = (value) => {
		if (!value) {
			return 'N/A';
		}

		const dateValue = new Date(value);
		if (Number.isNaN(dateValue.getTime())) {
			return 'N/A';
		}

		return dateValue.toLocaleDateString(undefined, {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
		});
	};

	formatScore = (value) => {
		const score = Number(value);

		if (!Number.isFinite(score)) {
			return '0.00';
		}

		return score.toFixed(2);
	};

	handleMessageUser = (targetUserId, firstName, lastName) => {
		const queryParams = new URLSearchParams({
			chatUserId: targetUserId,
			firstName: firstName || '',
			lastName: lastName || ''
		}).toString();
		window.location.href = `/explore?${queryParams}`;
	};

	renderSearchResultCard = (result) => {
		const fullName = `${result.firstName || ''} ${result.lastName || ''}`.trim() || 'Unknown User';
		const matchedOn = Array.isArray(result.matchedKeywords) ? result.matchedKeywords : [];

		return (
			<article key={`${result.userId}`} className={styles.connectionCard}>
				<div className={styles.connectionHeaderRow}>
					<div>
						<h3 className={styles.connectionName}>{fullName}</h3>
						<span style={{ fontSize: '0.8rem', color: '#64748b', display: 'block', marginTop: '4px' }}>
							User ID: {result.userId}
						</span>
					</div>
					<span className={styles.scoreChip}>Score {this.formatScore(result.bestSimilarity ?? result.finalScore)}</span>
				</div>

				<p className={styles.connectionMeta} style={{ marginTop: '12px' }}>
					Average Similarity: {this.formatScore(result.averageSimilarity)} • Final Score: {this.formatScore(result.finalScore)}
				</p>

				{matchedOn.length > 0 && (
					<div className={styles.matchTagWrap}>
						{matchedOn.map((item) => (
							<span key={`${result.userId}-${item}`} className={styles.matchTag}>
								{item}
							</span>
						))}
					</div>
				)}

				<div className={styles.cardActionsRow}>
					<button
						className={styles.messageButton}
						onClick={() => this.handleMessageUser(result.userId, result.firstName, result.lastName)}
					>
						<FiMessageSquare style={{ marginRight: '6px' }} /> Message
					</button>
				</div>
			</article>
		);
	};

	render() {
		const {
			isLoading,
			isSearchMode,
			errorText,
			queryText,
			parsedQuery,
			searchResults,
			isFocused,
		} = this.state;

		const showCompactLayout = isSearchMode;

		const pageClass = showCompactLayout
			? `${styles.page} ${styles.pageResultMode}`
			: styles.page;

		const optionsList = [
			{
				text: "Find me people with similar skills",
				actionText: "Search skills",
				icon: FiUsers,
			},
			{
				text: "Show me people working on open source projects",
				actionText: "Search GitHub",
				icon: FiCpu,
			},
			{
				text: "What are people at top AI companies working on?",
				actionText: "Search AI",
				icon: FiCompass,
			},
		];

		return (
			<div className={pageClass}>
				<Sidebar />

				<div className={styles.bgGlowTop} />
				<div className={styles.bgGlowBottom} />
				<div className={styles.bgGrid} />

				<div className={styles.layoutShell}>
					
					{/* Header Block shows only in initial view */}
					{!showCompactLayout && (
						<header className={styles.headerBlock}>
							<h1 className={styles.title}>
								<span className={styles.titleGradiant}>What are you looking for?</span>
							</h1>
							<p className={styles.subtitle}>Ask about People, Skills, and What's New</p>
						</header>
					)}

					{/* Suggested Quick Options */}
					{!showCompactLayout && (
						<section className={styles.optionsGrid}>
							{optionsList.map((option, index) => {
								const IconComp = option.icon;
								return (
									<button
										key={index}
										className={styles.optionCard}
										onClick={() => this.onOptionClick(option.text)}
										type="button"
									>
										<p className={styles.optionText}>
                                            <IconComp style={{ fontSize: '1rem', marginRight: '4px' }} />
                                            {option.text}
                                        </p>
										{/* <span className={styles.optionAction}>
											<IconComp style={{ fontSize: '1rem', marginRight: '4px' }} />
											{option.actionText}
										</span> */}
									</button>
								);
							})}
						</section>
					)}

					{/* Compact Heading when in Search Mode */}
					{showCompactLayout && (
						<div style={{ alignSelf: 'flex-start', marginBottom: '24px', animation: 'fadeIn 0.5s ease-out' }}>
							<h2 style={{ margin: '0 0 4px 0', fontSize: '1.4rem', fontWeight: 800 }}>Ask HQVibe</h2>
							<p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b' }}>Explore connections across the dynamic network map</p>
						</div>
					)}

					{/* Search Box */}
					<div className={styles.searchArea}>
						<div className={`${styles.inputOuterWrap} ${isFocused ? styles.inputOuterWrapFocused : ''}`}>
							<input
								className={styles.textbox}
								value={queryText}
								onChange={this.onQueryTextChange}
								onKeyDown={this.onKeyDown}
								onFocus={this.onFocus}
								onBlur={this.onBlur}
								placeholder="Ask HQVibe"
								disabled={isLoading}
							/>
							<button
								className={styles.submitBtn}
								onClick={this.runNaturalLanguageSearch}
								disabled={isLoading || !queryText.trim()}
								type="button"
								title="Ask Intelligence"
							>
								<FiArrowRight className={styles.submitIcon} />
							</button>
						</div>
					</div>

					{/* Results / Loading States Panel */}
					{isSearchMode && (
						<section className={styles.resultsSection}>
							
							{/* Error Message */}
							{errorText && (
								<div className={styles.errorBar}>
									{errorText}
								</div>
							)}

							{/* Loading Shimmer Card List */}
							{isLoading && (
								<div className={styles.loaderContainer}>
									<div className={styles.shimmerCard} />
									<div className={styles.shimmerCard} />
									<div className={styles.shimmerCard} />
								</div>
							)}

							{/* Parsed Query info from backend */}
							{!isLoading && parsedQuery && (
								<div className={styles.parsedQueryBox}>
									<strong>Interpreted Intent:</strong> {parsedQuery.type === 'p' ? 'General query' : parsedQuery.type === 's' ? 'Skill context' : parsedQuery.type === 'ss' ? 'Soft skill context' : parsedQuery.type === 't' ? 'Tool context' : parsedQuery.type === 'd' ? 'Domain context' : parsedQuery.type === 'i' ? 'Impact context' : 'Achievement context'} with keyword filter: <strong>{parsedQuery.keywords ? parsedQuery.keywords.join(', ') : ''}</strong>
								</div>
							)}

							{/* Search results display */}
							{!isLoading && !errorText && (
								<>
									<div className={styles.resultsMeta}>
										<span className={styles.resultsCount}>
											Found {searchResults.length} relevant connection{searchResults.length === 1 ? '' : 's'}
										</span>
										<button
											className={styles.clearSearchBtn}
											onClick={this.resetSearchMode}
											type="button"
										>
											<FiX style={{ marginRight: '4px', verticalAlign: 'middle' }} /> Clear Chat
										</button>
									</div>

									{searchResults.length === 0 ? (
										<div className={styles.statusMessage}>
											No direct connections matched this specific query. Try redefining skills, locations, or projects in your search.
										</div>
									) : (
										<div className={styles.resultsGrid}>
											{searchResults.map((result) => this.renderSearchResultCard(result))}
										</div>
									)}
								</>
							)}
						</section>
					)}

				</div>
			</div>
		);
	}
}
